import React from 'react';
import Immutable from 'immutable';
import { Webform } from '@uncinc/react-drupal-core';
import { useSelector } from 'react-redux';

import * as Sentry from '@sentry/react';
import {
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';

import { WEBFORM_CATEGORIES, SSO_RISK_MONITOR_REQUEST_WEBFORM_ID } from '../../../config/constants';
import WebformAssessment from '../Assessment';
import WebformDate from '../Date';
import WebformHidden from '../Hidden';
import WebformQuiz from '../Quiz';
import WebformQuizCheckboxes from '../QuizCheckboxes';
import WebformQuizRadios from '../QuizRadios';
import WebformQuizRadiosWithImage from '../QuizRadiosWithImage';
import WebformRadiosWithImage from '../RadiosWithImage';
import WebformRedirect from '../Redirect';
import WebformWizardPage from '../WizardPage';
import WebformApplicationRequest from '../ApplicationRequest';

import RiskMonitorCalculatorBlock from '../ApplicationRequest/RiskMonitorCalculatorBlock';
import RiskMonitorWebformNumberField from '../ApplicationRequest/RiskMonitorWebformNumberField';
import RiskMonitorWebformFieldset from '../ApplicationRequest/RiskMonitorWebformFieldset';

import './style.scss';

export const WebformRenderer = (props) => {
  const {
    id,
    categories,
    overriddenComponents,
    onSubmit,
    wrapInContainer,
    ...restProps
  } = props;

  if (categories && categories.includes(WEBFORM_CATEGORIES.QUIZ)) {
    return (
      <WebformQuiz quizId={id}>
        {({ setShowSubmission }) => (
          <Webform
            {...restProps}
            id={id}
            addTransitions
            overriddenComponents={{
              ...overriddenComponents,
              webform_actions: WebformQuiz.Actions,
              wizard_page: WebformWizardPage,
              webform_wizard_page: WebformWizardPage,
            }}
            onSubmit={() => {
              setShowSubmission(true);
              onSubmit();
            }}
          />
        )}
      </WebformQuiz>
    );
  }

  if (categories && categories.includes(WEBFORM_CATEGORIES.ASSESSMENT)) {
    return (
      <WebformAssessment id={id}>
        <Webform
          {...restProps}
          id={id}
          addTransitions
          overriddenComponents={{
            ...overriddenComponents,
            webform_actions: WebformAssessment.Actions,
            wizard_page: WebformWizardPage,
            webform_wizard_page: WebformWizardPage,
            radios: WebformAssessment.RadioButtons,
            processed_text: WebformAssessment.ProcessedText,
          }}
        />
      </WebformAssessment>
    );
  }

  if (id === SSO_RISK_MONITOR_REQUEST_WEBFORM_ID) {
    // scrollToTopAfterSubmit does not work properly for this form.
    // https://uncinc.monday.com/boards/1674548856/pulses/8245578637.
    const scrollOverwrite = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    };

    return (
      <WebformApplicationRequest id={id}>
        <Webform
          {...restProps}
          id={id}
          overriddenComponents={{
            ...overriddenComponents,
            hidden: RiskMonitorCalculatorBlock,
            number: (data) => RiskMonitorWebformNumberField(data),
            fieldset: RiskMonitorWebformFieldset,
          }}
          scrollToTopAfterSubmit={false}
          onSuccessfullSubmit={scrollOverwrite}
          onFailedSubmit={scrollOverwrite}
        />
      </WebformApplicationRequest>
    );
  }

  const WebformRenderer = (
    <Webform
      {...restProps}
      id={id}
      overriddenComponents={overriddenComponents}
      onSubmit={onSubmit}
    />
  );

  return wrapInContainer ? (
    <Container small>
      {WebformRenderer}
    </Container>
  ) : WebformRenderer;
};

const WebformWrapper = ({
  id,
  wrapInContainer = false,
  overriddenComponents = {},
  ...restProps
}) => {
  const status = useSelector((state) => state.getIn(['webform', id, 'info', 'status'], ''));
  const categories = useSelector((state) => state.getIn(['webform', id, 'info', 'categories'], Immutable.List()));

  const overriddenComponentsWebform = {
    redirect: WebformRedirect,
    hidden: WebformHidden,
    date: WebformDate,
    quiz_checkboxes: WebformQuizCheckboxes,
    quiz_radios: WebformQuizRadios,
    quiz_radios_with_image: WebformQuizRadiosWithImage,
    radios_with_image: WebformRadiosWithImage,
    ...overriddenComponents,
  };

  const onSubmit = () => {
    if (window) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'form.submit',
        formId: id,
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const onFailedSubmit = (data) => {
    if (data instanceof Error) {
      // Network error or similar.
      Sentry.captureException(data);
    } else {
      // Webform error. Only get the keys, since the values may contain personal data.
      Sentry.withScope((scope) => {
        const dataSanitized = JSON.stringify(Object.keys(data));
        scope.setExtra('fields', dataSanitized);
        Sentry.captureException(new Error('Webform submission error due to invalid values'));
      });
    }
  };

  return (
    <div className="Webform__wrapper">
      <WebformRenderer
        {...restProps}
        id={id}
        status={status}
        categories={categories}
        overriddenComponents={overriddenComponentsWebform}
        onSubmit={onSubmit}
        scrollToTopAfterMultiFormStep={false}
        onPrevMultiFormStep={scrollToTop}
        onNextMultiFormStep={scrollToTop}
        onFailedSubmit={onFailedSubmit}
        wrapInContainer={wrapInContainer}
        includeFieldSets
      />
    </div>
  );
};

export default WebformWrapper;
